export type InboxNotificationLike = {
  notificationId: string;
  archived: boolean;
  incidentId: string | null;
};

export type NotificationReadIds = Record<string, string>;

export type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

const EMPTY_READ_IDS: NotificationReadIds = {};

/**
 * Matches the Hub badge's special-case for the Memphis demo inbox row so
 * incident-scoped counts stay consistent without importing that demo module.
 */
export const ALWAYS_IN_SCOPE_NOTIFICATION_IDS = new Set([
  'demo-memphis-flood-power-outage',
  'demo-fema-thunderbolt-workspace-invite',
]);

type Listener = () => void;

const listeners = new Set<Listener>();
const snapshotByKey = new Map<string, NotificationReadIds>();

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeNotificationReadState(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function readStorageKey(userGuid: string, groupGuid: string): string {
  return `pratus:notifications:read:${userGuid}:${groupGuid}`;
}

export function loadReadIds(
  key: string,
  storage: StorageLike | null = defaultStorage()
): NotificationReadIds {
  if (!key || !storage) return {};
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const next: NotificationReadIds = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (typeof value === 'string') next[id] = value;
    }
    return next;
  } catch {
    return {};
  }
}

export function getReadIdsSnapshot(key: string): NotificationReadIds {
  if (!key) return EMPTY_READ_IDS;
  const cached = snapshotByKey.get(key);
  if (cached) return cached;
  const loaded = loadReadIds(key);
  snapshotByKey.set(key, loaded);
  return loaded;
}

export function pruneReadIds(
  readIds: NotificationReadIds,
  inboxIds: Iterable<string>
): NotificationReadIds {
  const keep = inboxIds instanceof Set ? inboxIds : new Set(inboxIds);
  const next: NotificationReadIds = {};
  for (const [id, timestamp] of Object.entries(readIds)) {
    if (keep.has(id)) next[id] = timestamp;
  }
  return next;
}

function persistReadIds(
  key: string,
  next: NotificationReadIds,
  storage: StorageLike | null
) {
  storage?.setItem(key, JSON.stringify(next));
  snapshotByKey.set(key, next);
  emit();
}

export function markRead(
  key: string,
  ids: readonly string[],
  options?: {
    inboxIds?: Iterable<string>;
    now?: () => string;
    storage?: StorageLike | null;
  }
): NotificationReadIds {
  const storage =
    options?.storage === undefined ? defaultStorage() : options.storage;
  const current = loadReadIds(key, storage);
  const timestamp = (options?.now ?? (() => new Date().toISOString()))();
  const next: NotificationReadIds = { ...current };
  for (const id of ids) {
    if (!(id in next)) next[id] = timestamp;
  }
  const pruned = options?.inboxIds
    ? pruneReadIds(next, options.inboxIds)
    : next;
  persistReadIds(key, pruned, storage);
  return pruned;
}

export function isActiveInboxNotification(
  notification: InboxNotificationLike,
  incidentId?: string | null
): boolean {
  if (notification.archived) return false;
  if (ALWAYS_IN_SCOPE_NOTIFICATION_IDS.has(notification.notificationId)) {
    return true;
  }
  if (!incidentId) return true;
  return notification.incidentId === incidentId;
}

export function unreadNotificationIds(
  notifications: InboxNotificationLike[],
  readIds: NotificationReadIds,
  incidentId?: string | null
): string[] {
  return notifications
    .filter(
      (notification) =>
        isActiveInboxNotification(notification, incidentId) &&
        !(notification.notificationId in readIds)
    )
    .map((notification) => notification.notificationId);
}

export function countUnreadNotifications(
  notifications: InboxNotificationLike[],
  readIds: NotificationReadIds,
  incidentId?: string | null
): number {
  return unreadNotificationIds(notifications, readIds, incidentId).length;
}
